import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useFormat } from "@/contexts/FormatContext";
import { enablePush, pushEnabled } from "@/lib/push";
import { toast } from "sonner";
import { Loader2, Briefcase, Layers, TrendingUp, Megaphone, AlertTriangle, Crosshair } from "lucide-react";
import OverviewTab from "./portfolio/OverviewTab";
import TradeDeskTab from "./portfolio/TradeDeskTab";
import ExposureTab from "./portfolio/ExposureTab";
import WaiversTab from "./portfolio/WaiversTab";
import AlertsTab from "./portfolio/AlertsTab";

const TABS = [
  { id: "overview", label: "Overview", icon: Layers },
  { id: "tradedesk", label: "Trade Desk", icon: Crosshair },
  { id: "exposure", label: "Exposure", icon: TrendingUp },
  { id: "waivers", label: "Waiver Scan", icon: Megaphone },
  { id: "alerts", label: "Alerts", icon: AlertTriangle },
];

export default function Portfolio() {
  const { format } = useFormat();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.some(t => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const [tab, setTabState] = useState(initialTab);
  const setTab = (id) => { setTabState(id); setSearchParams(id === "overview" ? {} : { tab: id }, { replace: true }); };
  const [loading, setLoading] = useState(true);
  const [ov, setOv] = useState(null);
  const [waivers, setWaivers] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [tradeDesk, setTradeDesk] = useState(null);
  const [pitches, setPitches] = useState({});
  const [pitchLoading, setPitchLoading] = useState({});
  const [watch, setWatch] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem("pf_empty_banner_dismissed") === "1");
  const dismissBanner = () => { localStorage.setItem("pf_empty_banner_dismissed", "1"); setBannerDismissed(true); };
  const restoreAll = () => updateSettings({ excluded_leagues: [] }, true);
  const snooze = (days) => updateSettings({ alerts_snoozed_until: new Date(Date.now() + days * 86400000).toISOString() });
  const resumeAlerts = () => updateSettings({ alerts_snoozed_until: "" });

  useEffect(() => { pushEnabled().then(setPushOn).catch(() => {}); }, []);
  useEffect(() => { api.get("/portfolio/settings").then(r => setSettings(r.data)).catch(() => {}); }, []);

  const updateSettings = async (patch, reload = false) => {
    const r = await api.put("/portfolio/settings", patch);
    setSettings(r.data);
    if (reload) loadOverview(true);
    return r.data;
  };
  const toggleLeague = (l) => {
    const ex = new Set(settings?.excluded_leagues || []);
    ex.has(l.league_id) ? ex.delete(l.league_id) : ex.add(l.league_id);
    updateSettings({ excluded_leagues: [...ex] }, true);
  };
  const excludeEmpty = () => {
    const empty = (ov?.leagues || []).filter(l => l.value === 0).map(l => l.league_id);
    const ex = new Set([...(settings?.excluded_leagues || []), ...empty]);
    updateSettings({ excluded_leagues: [...ex] }, true);
  };

  const enableBrowserAlerts = async () => {
    setPushBusy(true);
    try { await enablePush(); setPushOn(true); toast.success("Browser alerts enabled"); }
    catch (e) { toast.error(e.message || "Could not enable alerts"); }
    finally { setPushBusy(false); }
  };
  const sendTestAlert = async () => {
    setPushBusy(true);
    try {
      const r = await api.post("/portfolio/notify-test", {});
      toast.success(r.data.sent > 0 ? `Test alert sent (${r.data.player || "sample"})` : "Enable browser alerts first to receive it");
    } catch { toast.error("Could not send test alert"); }
    finally { setPushBusy(false); }
  };

  const loadOverview = useCallback((soft = false) => {
    soft ? setSettingsBusy(true) : setLoading(true);
    api.get("/portfolio").then(r => {
      setOv(r.data);
    }).finally(() => { soft ? setSettingsBusy(false) : setLoading(false); });
  }, []);

  useEffect(() => { loadOverview(); }, [format, loadOverview]);

  useEffect(() => {
    if (tab === "waivers" && waivers === null) api.get("/portfolio/waivers").then(r => setWaivers(r.data.waivers));
    if (tab === "alerts" && alerts === null) api.get("/portfolio/alerts").then(r => setAlerts(r.data.alerts));
    if (tab === "tradedesk" && tradeDesk === null) api.get("/portfolio/trade-desk").then(r => setTradeDesk(r.data));
    if (tab === "tradedesk" && watch === null) api.get("/portfolio/trade-desk/watch").then(r => setWatch(r.data.watchlist || []));
  }, [tab]); // eslint-disable-line

  const copyBlueprint = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Trade blueprint copied — paste it on Sleeper"); }
    catch { toast.error("Couldn't copy. Long-press to select the text."); }
  };

  const writePitch = async (i, t) => {
    setPitchLoading(p => ({ ...p, [i]: true }));
    try {
      const r = await api.post("/portfolio/trade-desk/pitch", {
        league_id: t.league_id, owner_roster_id: t.owner_roster_id,
        target: { name: t.name, position: t.position, value: t.value },
        offer: { you_give: t.suggested_offer?.you_give || [] }, fmt: t.format,
      });
      setPitches(p => ({ ...p, [i]: r.data.pitch }));
    } catch { toast.error("AI pitch is unavailable right now. Please try again."); }
    finally { setPitchLoading(p => ({ ...p, [i]: false })); }
  };

  const isWatched = (t) => (watch || []).some(w => w.league_id === t.league_id && w.player_id === t.player_id);

  const toggleWatch = async (t) => {
    const on = isWatched(t);
    try {
      if (on) {
        await api.post("/portfolio/trade-desk/unwatch", { league_id: t.league_id, player_id: t.player_id });
        setWatch(w => (w || []).filter(x => !(x.league_id === t.league_id && x.player_id === t.player_id)));
        toast.success("Removed from watchlist");
      } else {
        await api.post("/portfolio/trade-desk/watch", {
          league_id: t.league_id, league_name: t.league_name, owner_roster_id: t.owner_roster_id,
          owner_name: t.owner_name, kind: t.kind, player_id: t.player_id, name: t.name,
          position: t.position, value: t.value, fmt: t.format,
        });
        const r = await api.get("/portfolio/trade-desk/watch");
        setWatch(r.data.watchlist || []);
        toast.success("Watching — we'll alert you when a buy-low window opens");
      }
    } catch { toast.error("Couldn't update watchlist"); }
  };

  if (loading) return <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>;

  const notConnected = !ov || !ov.connected || (ov.leagues || []).length === 0;

  return (
    <div className="space-y-6 fade-up">
      <header>
        <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// PORTFOLIO</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight" data-testid="portfolio-heading">Multi-League Portfolio</h1>
        <p className="text-[var(--text-2)] mt-2 max-w-2xl">Every synced Sleeper league in one command center — exposure, positional concentration, and consolidated waiver scans across your current-season rosters.</p>
      </header>

      {notConnected ? (
        <div className="card-bordered p-10 text-center" data-testid="portfolio-empty">
          <Briefcase className="w-8 h-8 text-[var(--text-3)] mx-auto mb-3" />
          <div className="font-display text-xl font-bold mb-2">No portfolio yet</div>
          <p className="text-sm text-[var(--text-2)] max-w-md mx-auto mb-5">
            Connect your Sleeper account and sync your leagues to unlock exposure tracking across all of them.
          </p>
          <Link to="/app/power-rankings" className="btn-primary text-sm inline-block" data-testid="portfolio-connect-link">Sync Sleeper leagues</Link>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 border-b border-[var(--border)] pb-3">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ptab-${t.id}`}
                className={`text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-2 transition-colors ${tab === t.id ? "bg-[var(--accent)] text-black font-semibold" : "text-[var(--text-2)] hover:text-white border border-[var(--border)]"}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "overview" && (
            <OverviewTab ov={ov} settings={settings} bannerDismissed={bannerDismissed} settingsBusy={settingsBusy}
              dismissBanner={dismissBanner} excludeEmpty={excludeEmpty} restoreAll={restoreAll} toggleLeague={toggleLeague} />
          )}

          {tab === "tradedesk" && (
            <TradeDeskTab tradeDesk={tradeDesk} watch={watch} pitches={pitches} pitchLoading={pitchLoading}
              copyBlueprint={copyBlueprint} writePitch={writePitch} toggleWatch={toggleWatch} isWatched={isWatched} />
          )}

          {tab === "exposure" && <ExposureTab ov={ov} />}

          {tab === "waivers" && <WaiversTab waivers={waivers} />}

          {tab === "alerts" && (
            <AlertsTab alerts={alerts} settings={settings} setSettings={setSettings} updateSettings={updateSettings}
              pushOn={pushOn} pushBusy={pushBusy} enableBrowserAlerts={enableBrowserAlerts} sendTestAlert={sendTestAlert}
              snooze={snooze} resumeAlerts={resumeAlerts} />
          )}
        </>
      )}
    </div>
  );
}
